import { useEffect, useMemo, useState } from "react";
import toast from "../../utils/toast";
import axios from "../../lib/axios";
import { BACKEND_URL } from "../../config";
import { safeGetItem } from "../../utils/storage";

const statusStyles = {
  active: "bg-emerald-500/15 text-emerald-200 border border-emerald-400/30",
  expired: "bg-rose-500/15 text-rose-200 border border-rose-400/30",
  pending: "bg-amber-500/15 text-amber-200 border border-amber-400/30",
};

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [viewedOffer, setViewedOffer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const token = safeGetItem("token");
      if (!token) throw new Error("Admin token missing");
      const res = await axios.get(`${BACKEND_URL}/admin/offers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("OFFERS:", res.data);
      setOffers(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, []);

  const filtered = useMemo(() => {
    return offers.filter((offer) => {
      const searchText = search.toLowerCase();
      const matchesSearch =
        (offer.title || "").toLowerCase().includes(searchText) ||
        (offer.consultancyName || "").toLowerCase().includes(searchText);
      const status = (offer.status || "active").toLowerCase();
      const matchesStatus = statusFilter === "All" || status === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [offers, search, statusFilter]);

  const viewOffer = async (id) => {
    try {
      const token = safeGetItem("token");
      if (!token) throw new Error("Admin token missing");
      const res = await axios.get(`${BACKEND_URL}/admin/offer/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setViewedOffer(res.data);
      setIsModalOpen(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to load offer details");
    }
  };

  const deleteOffer = async (id) => {
    if (!window.confirm("Delete this offer?")) return;
    try {
      const token = safeGetItem("token");
      if (!token) throw new Error("Admin token missing");
      await axios.delete(`${BACKEND_URL}/admin/offer/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Offer deleted");
      fetchOffers();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to delete offer");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Global Offers</h2>
          <p className="text-white/60 text-sm">
            Track every public campaign launched by consultancies.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search offers / consultancies"
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder-white/50"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white"
          >
            {["All", "Active", "Pending", "Expired"].map((status) => (
              <option key={status} value={status} className="bg-slate-900">
                {status}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur divide-y divide-white/5">
        <div className="grid grid-cols-6 gap-4 px-6 py-3 text-xs uppercase tracking-[3px] text-white/60">
          <span>Consultancy</span>
          <span>Offer</span>
          <span>Discount</span>
          <span>Status</span>
          <span>End Date</span>
          <span>Actions</span>
        </div>
        {loading && <div className="p-6 text-center text-white/70">Loading offers...</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-6 text-center text-white/60">No offers found.</div>
        )}
        {!loading &&
          filtered.map((offer) => {
            const status = (offer.status || "active").toLowerCase();
            return (
              <div
                className="grid grid-cols-6 gap-4 px-6 py-4 items-center hover:bg-white/5 transition"
                key={offer._id}
              >
                <div>
                  <p className="font-semibold">{offer.consultancyName || "Unknown consultancy"}</p>
                </div>
                <p>{offer.title || "Untitled offer"}</p>
                <p>{offer.discount ?? 0}%</p>
                <span className={`px-3 py-1 rounded-full text-xs ${statusStyles[status] || statusStyles.active}`}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </span>
                <p>
                  {offer.endDate ? new Date(offer.endDate).toLocaleDateString() : "No deadline"}
                </p>
                <div className="flex gap-2 text-sm">
                  <button
                    className="px-3 py-1 rounded-2xl border border-white/10 hover:bg-white/10"
                    onClick={() => viewOffer(offer._id)}
                  >
                    View
                  </button>
                  <button
                    className="px-3 py-1 rounded-2xl border border-rose-400/30 text-rose-200 hover:bg-rose-500/10"
                    onClick={() => deleteOffer(offer._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-lg rounded-3xl bg-slate-900 border border-white/10 p-6 relative">
            <button
              className="absolute top-4 right-4 text-white/60 hover:text-white"
              onClick={() => {
                setIsModalOpen(false);
                setViewedOffer(null);
              }}
            >
              ✕
            </button>
            {viewedOffer ? (
              <div className="space-y-4 text-white">
                <h3 className="text-2xl font-semibold">{viewedOffer.title || "Untitled offer"}</h3>
                <p className="text-white/70 text-sm">
                  Consultancy: {viewedOffer.consultancyName || "Unknown"}
                </p>
                <p className="text-sm text-white/70">{viewedOffer.description || "No description"}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-white/60">Discount</p>
                    <p>{viewedOffer.discount ?? 0}%</p>
                  </div>
                  <div>
                    <p className="text-white/60">Status</p>
                    <p>{(viewedOffer.status || "Active").toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="text-white/60">End Date</p>
                    <p>
                      {viewedOffer.endDate
                        ? new Date(viewedOffer.endDate).toLocaleDateString()
                        : "No deadline"}
                    </p>
                  </div>
                  <div>
                    <p className="text-white/60">Created</p>
                    <p>
                      {viewedOffer.createdAt
                        ? new Date(viewedOffer.createdAt).toLocaleString()
                        : "—"}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-16 text-center text-white/70">No details available.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
